import { useState, useEffect } from 'react';
import { Calculator, Plus, Trash2, Save, Download, List, Eye, LogOut, Users } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import UserManagement from './components/UserManagement';
import { initGA4, trackCalculation, trackRatePlanCreated, trackRatePlanViewed, trackRatePlanDeleted, trackRatePlanUpdated, trackTabView } from './utils/analytics';

// Type definitions
interface AdditionalCost {
    id: number;
    name: string;
    amount: number;
}

interface RatePlan {
    id: number;
    name: string;
    description?: string;
    mrcCost: number;
    dataAmountMB: number;
    costPerMB: number;
    marginPercent: number;
    additionalCosts?: AdditionalCost[];
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export default function MVNOPlatform() {
  const { user, token, logout, authenticatedFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('calculator');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Calculator state
  const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [mrcCost, setMrcCost] = useState('');
  const [costPerMB, setCostPerMB] = useState('');
  const [dataAmountMB, setDataAmountMB] = useState('');
  const [margin, setMargin] = useState('');
  const [additionalCosts, setAdditionalCosts] = useState<AdditionalCost[]>([]);
  const [newCostName, setNewCostName] = useState('');
  const [newCostAmount, setNewCostAmount] = useState('');
  const [savedPlans, setSavedPlans] = useState<RatePlan[]>([]);
  const [saveStatus, setSaveStatus] = useState<string>('');

  // Initialize Google Tag Manager
  useEffect(() => {
      initGA4();
  }, []);

  useEffect(() => {
    if (activeTab === 'plans') {
      loadSavedPlans();
    }
    // Track tab view in GA4
    trackTabView(activeTab);
  }, [activeTab]);

  const loadSavedPlans = async () => {
      setLoading(true);
      setError('');
      try {
          const response = await authenticatedFetch(`${API_BASE_URL}/rate-plans`, {
              headers: {
                  'X-Tenant-Id': user?.tenantId.toString() || '1',
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
              }
          });

          if (response.ok) {
              const plans = await response.json();
              setSavedPlans(plans);
          } else {
              setError('Failed to load saved plans');
          }
      } catch (err: any) {
          console.error('Error loading plans:', err);
          setError(err.message || 'Unable to connect to server. Running in offline mode.');
          setSavedPlans([]);
      } finally {
          setLoading(false);
      }
  };

  const addCost = () => {
    if (!newCostName.trim()) {
      setError('Please enter a cost name');
      return;
    }
    if (!newCostAmount || parseFloat(newCostAmount) < 0) {
      setError('Please enter a valid cost amount');
      return;
    }
    
    setAdditionalCosts([
      ...additionalCosts,
      { 
        id: Date.now(), 
        name: newCostName.trim(), 
        amount: parseFloat(newCostAmount) 
      }
    ]);
    setNewCostName('');
    setNewCostAmount('');
    setError('');
  };

const removeCost = (id: number) => {
    setAdditionalCosts(additionalCosts.filter(cost => cost.id !== id));
};

  const calculateRetailPrice = () => {
    const mrc = parseFloat(mrcCost) || 0;
    const perMB = parseFloat(costPerMB) || 0;
    const dataMB = parseFloat(dataAmountMB) || 0;
    const totalAdditional = additionalCosts.reduce((sum, cost) => sum + cost.amount, 0);
    const marginPercent = parseFloat(margin) || 0;
    const regulatoryPercent = 32;
    
    const dataCost = perMB * dataMB;
    const totalCost = mrc + dataCost + totalAdditional;
    const retailPriceBeforeTax = totalCost * (1 + marginPercent / 100);
    const regulatoryCost = retailPriceBeforeTax * (regulatoryPercent / 100);
    const retailPrice = retailPriceBeforeTax + regulatoryCost;
    
    return {
      mrc,
      perMB,
      dataMB,
      dataCost,
      totalAdditional,
      totalCost,
      retailPriceBeforeTax,
      regulatoryPercent,
      regulatoryCost,
      retailPrice,
      marginAmount: retailPriceBeforeTax - totalCost
    };
  };

  const results = calculateRetailPrice();

  const validateForm = () => {
    if (!planName.trim()) {
      setSaveStatus('Please enter a plan name');
      return false;
    }
    if (!mrcCost || parseFloat(mrcCost) < 0) {
      setSaveStatus('Please enter a valid MRC cost');
      return false;
    }
    if (!costPerMB || parseFloat(costPerMB) < 0) {
      setSaveStatus('Please enter a valid cost per MB');
      return false;
    }
    if (!dataAmountMB || parseInt(dataAmountMB) < 0) {
      setSaveStatus('Please enter a valid data amount');
      return false;
    }
    if (!margin || parseFloat(margin) < 0) {
      setSaveStatus('Please enter a valid margin percentage');
      return false;
    }
    return true;
  };

  const savePlan = async () => {
    if (!validateForm()) {
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }

    const planData = {
      name: planName.trim(),
      description: planDescription.trim(),
      mrcCost: parseFloat(mrcCost),
      costPerMB: parseFloat(costPerMB),
      dataAmountMB: parseInt(dataAmountMB),
      marginPercent: parseFloat(margin),
      additionalCosts: additionalCosts.map(cost => ({
        name: cost.name,
        amount: cost.amount
      })),
      active: true
    };

    const isUpdating = editingPlanId !== null;
    const url = isUpdating
      ? `${API_BASE_URL}/rate-plans/${editingPlanId}`
      : `${API_BASE_URL}/rate-plans`;
    const method = isUpdating ? 'PUT' : 'POST';

    setLoading(true);
    try {
      const response = await authenticatedFetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': user?.tenantId.toString() || '1',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(planData)
      });

      if (response.ok) {
        const savedPlan = await response.json();
        setSaveStatus(isUpdating ? '✓ Plan updated successfully!' : '✓ Plan saved successfully!');

        // Track rate plan creation or update in GA4
        if (!isUpdating && savedPlan?.id) {
          trackRatePlanCreated(savedPlan.id, planName.trim());
        } else if (isUpdating && editingPlanId) {
          trackRatePlanUpdated(editingPlanId, planName.trim());
        }

        setTimeout(() => {
          setSaveStatus('');
          resetForm();
        }, 2000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSaveStatus('Error: ' + (errorData.message || 'Failed to save plan'));
      }
    } catch (err) {
      console.error('Error saving plan:', err);
      setSaveStatus('Error: Unable to connect to server');
    } finally {
      setLoading(false);
      setTimeout(() => setSaveStatus(''), 5000);
    }
  };

  const resetForm = () => {
    setEditingPlanId(null);
    setPlanName('');
    setPlanDescription('');
    setMrcCost('');
    setCostPerMB('');
    setDataAmountMB('');
    setMargin('');
    setAdditionalCosts([]);
    setError('');
  };

  const loadPlan = (plan : RatePlan) => {
    setEditingPlanId(plan.id);
    setPlanName(plan.name);
    setPlanDescription(plan.description || '');
    setMrcCost(plan.mrcCost.toString());
    setCostPerMB(plan.costPerMB.toString());
    setDataAmountMB(plan.dataAmountMB.toString());
    setMargin(plan.marginPercent.toString());
    setAdditionalCosts((plan.additionalCosts || []).map((cost: AdditionalCost) => ({
        id: cost.id,
        name: cost.name,
        amount: cost.amount
    })));

    // Track rate plan viewing in GA4
    trackRatePlanViewed(plan.id, plan.name);

    setActiveTab('calculator');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deletePlan = async (planId: number, planName: string) => {
    if (!confirm(`Are you sure you want to delete "${planName}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/rate-plans/${planId}`, {
        method: 'DELETE',
        headers: {
          'X-Tenant-Id': user?.tenantId.toString() || '1',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Track rate plan deletion in GA4
        trackRatePlanDeleted(planId, planName);

        // Remove the plan from the local state
        setSavedPlans(savedPlans.filter(plan => plan.id !== planId));
        // Show success message
        setError('');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError('Error: ' + (errorData.error || 'Failed to delete plan'));
      }
    } catch (err) {
      console.error('Error deleting plan:', err);
      setError('Error: Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const exportPricing = () => {
    const timestamp = new Date().toLocaleString();
    const exportPlanName = planName.trim() || 'Unnamed Plan';
    const exportDescription = planDescription.trim();

    // Track calculation/export in GTM
    trackCalculation(results.retailPrice);

    let csv = 'MVNO Rate Plan Pricing Calculator - Export\n';
    csv += `Plan Name:,"${exportPlanName}"\n`;
    if (exportDescription) {
      csv += `Description:,"${exportDescription}"\n`;
    }
    csv += `Export Date:,${timestamp}\n\n`;
    csv += 'Wholesale Cost Breakdown\n';
    csv += `MRC (Monthly Recurring Cost):,$${results.mrc.toFixed(2)}\n`;
    csv += `Cost per MB:,$${results.perMB.toFixed(5)}\n`;
    csv += `Data Amount (MB):,${results.dataMB.toFixed(0)}\n`;
    csv += `Total Data Cost:,$${results.dataCost.toFixed(2)}\n`;
    
    if (additionalCosts.length > 0) {
      csv += '\nAdditional Costs\n';
      additionalCosts.forEach(cost => {
        csv += `"${cost.name}":,$${cost.amount.toFixed(2)}\n`;
      });
      csv += `Total Additional:,$${results.totalAdditional.toFixed(2)}\n`;
    }
    
    csv += `\nTotal Cost:,$${results.totalCost.toFixed(2)}\n`;
    csv += `Margin (${parseFloat(margin) || 0}%):,$${results.marginAmount.toFixed(2)}\n`;
    csv += `Retail Price (before regulatory):,$${results.retailPriceBeforeTax.toFixed(2)}\n`;
    csv += `Regulatory Costs (${results.regulatoryPercent}%):,$${results.regulatoryCost.toFixed(2)}\n`;
    csv += `\nFinal Retail Price:,$${results.retailPrice.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mvno-pricing-${exportPlanName.replace(/\s+/g, '-')}-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Calculator className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white">MVNO Platform</h1>
                  <p className="text-blue-100 text-sm">Rate Plan Pricing Calculator</p>
                  <p className="text-blue-200 text-xs mt-1">
                    {user?.firstName || user?.email} ({user?.role})
                  </p>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap justify-center">
                <button
                  onClick={() => setActiveTab('calculator')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    activeTab === 'calculator'
                      ? 'bg-white text-indigo-600'
                      : 'bg-indigo-500 text-white hover:bg-indigo-400'
                  }`}
                >
                  <Calculator className="w-5 h-5 inline mr-2" />
                  Calculator
                </button>
                <button
                  onClick={() => setActiveTab('plans')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    activeTab === 'plans'
                      ? 'bg-white text-indigo-600'
                      : 'bg-indigo-500 text-white hover:bg-indigo-400'
                  }`}
                >
                  <List className="w-5 h-5 inline mr-2" />
                  Saved Plans
                </button>
                {user?.role === 'admin' && (
                  <button
                    onClick={() => setActiveTab('users')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      activeTab === 'users'
                        ? 'bg-white text-indigo-600'
                        : 'bg-indigo-500 text-white hover:bg-indigo-400'
                    }`}
                  >
                    <Users className="w-5 h-5 inline mr-2" />
                    Users
                  </button>
                )}
                <button
                  onClick={logout}
                  className="px-4 py-2 rounded-lg font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  <LogOut className="w-5 h-5 inline mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-8">
            {error && (
              <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded-lg">
                {error}
              </div>
            )}

            {activeTab === 'users' ? (
              <UserManagement />
            ) : activeTab === 'calculator' ? (
              <>
                {/* Plan Info */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Plan Information</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Plan Name *
                      </label>
                      <input
                        type="text"
                        value={planName}
                        onChange={(e) => setPlanName(e.target.value)}
                        placeholder="e.g., Basic 5GB Plan"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        value={planDescription}
                        onChange={(e) => setPlanDescription(e.target.value)}
                        placeholder="Brief description"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Wholesale Costs */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Wholesale Costs</h2>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        MRC ($) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={mrcCost}
                        onChange={(e) => setMrcCost(e.target.value)}
                        placeholder="3.50"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none text-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Cost per MB ($) *
                      </label>
                      <input
                        type="number"
                        step="0.00001"
                        min="0"
                        value={costPerMB}
                        onChange={(e) => setCostPerMB(e.target.value)}
                        placeholder="0.00307"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none text-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Data Amount (MB) *
                      </label>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={dataAmountMB}
                        onChange={(e) => setDataAmountMB(e.target.value)}
                        placeholder="5000"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none text-lg"
                      />
                    </div>
                  </div>
                </div>

                {/* Margin */}
                <div className="mb-8">
                  <div className="max-w-xs">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Margin (%) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={margin}
                      onChange={(e) => setMargin(e.target.value)}
                      placeholder="25"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none text-lg"
                    />
                  </div>
                </div>

                {/* Additional Costs */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Additional Costs</h2>
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <input
                      type="text"
                      value={newCostName}
                      onChange={(e) => setNewCostName(e.target.value)}
                      placeholder="Cost name (e.g., Support, Billing)"
                      className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newCostAmount}
                      onChange={(e) => setNewCostAmount(e.target.value)}
                      placeholder="Amount"
                      className="w-full sm:w-32 px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                    />
                    <button
                      onClick={addCost}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                    >
                      <Plus className="w-5 h-5" />
                      Add
                    </button>
                  </div>

                  {additionalCosts.length > 0 && (
                    <div className="space-y-2">
                      {additionalCosts.map((cost) => (
                        <div key={cost.id} className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
                          <span className="font-medium text-gray-700">{cost.name}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-900 font-semibold">${cost.amount.toFixed(2)}</span>
                            <button
                              onClick={() => removeCost(cost.id)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                              aria-label="Remove cost"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mb-8">
                  <button
                    onClick={savePlan}
                    disabled={loading}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-5 h-5" />
                    {loading ? 'Saving...' : 'Save Plan'}
                  </button>
                  <button
                    onClick={exportPricing}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-semibold"
                  >
                    <Download className="w-5 h-5" />
                    Export CSV
                  </button>
                  <button
                    onClick={resetForm}
                    className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold"
                  >
                    Clear Form
                  </button>
                  {saveStatus && (
                    <div className={`flex items-center px-4 py-3 rounded-lg font-semibold ${
                      saveStatus.startsWith('✓') || saveStatus.includes('success')
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {saveStatus}
                    </div>
                  )}
                </div>

                {/* Results */}
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border-2 border-indigo-200">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Pricing Breakdown</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-indigo-200">
                      <span className="text-gray-700">MRC:</span>
                      <span className="font-semibold text-gray-900">${results.mrc.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-indigo-200">
                      <span className="text-gray-700">Data Cost ({results.dataMB.toFixed(0)} MB × ${results.perMB.toFixed(5)}):</span>
                      <span className="font-semibold text-gray-900">${results.dataCost.toFixed(2)}</span>
                    </div>
                    {additionalCosts.length > 0 && (
                      <>
                        {additionalCosts.map((cost) => (
                          <div key={cost.id} className="flex justify-between items-center py-2 border-b border-indigo-200">
                            <span className="text-gray-700">{cost.name}:</span>
                            <span className="font-semibold text-gray-900">${cost.amount.toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between items-center py-2 border-b border-indigo-200">
                          <span className="text-gray-700">Total Additional Costs:</span>
                          <span className="font-semibold text-gray-900">${results.totalAdditional.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between items-center py-2 border-b border-indigo-200">
                      <span className="text-gray-700 font-semibold">Total Cost:</span>
                      <span className="font-bold text-gray-900">${results.totalCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-indigo-200">
                      <span className="text-gray-700">Margin ({parseFloat(margin) || 0}%):</span>
                      <span className="font-semibold text-green-600">+${results.marginAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-indigo-200">
                      <span className="text-gray-700 font-semibold">Retail Price (before regulatory):</span>
                      <span className="font-bold text-gray-900">${results.retailPriceBeforeTax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-indigo-200">
                      <span className="text-gray-700">Regulatory Costs ({results.regulatoryPercent}%):</span>
                      <span className="font-semibold text-orange-600">+${results.regulatoryCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 mt-4 bg-indigo-600 text-white rounded-lg px-4">
                      <span className="text-lg font-bold">Final Retail Price:</span>
                      <span className="text-2xl font-bold">${results.retailPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Saved Plans Tab */
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Saved Rate Plans</h2>
                  {savedPlans.length > 0 && (
                    <button
                      onClick={loadSavedPlans}
                      disabled={loading}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Loading...' : 'Refresh'}
                    </button>
                  )}
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    <p className="mt-4 text-gray-600">Loading plans...</p>
                  </div>
                ) : savedPlans.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <List className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No saved plans yet</p>
                    <p className="text-sm mt-2">Create and save a plan from the calculator</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {savedPlans.map((plan) => (
                      <div key={plan.id} className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-indigo-300 transition-colors">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                            {plan.description && (
                              <p className="text-gray-600 mb-4">{plan.description}</p>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">MRC:</span>
                                <span className="ml-2 font-semibold">${plan.mrcCost.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Data:</span>
                                <span className="ml-2 font-semibold">{plan.dataAmountMB} MB</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Cost/MB:</span>
                                <span className="ml-2 font-semibold">${plan.costPerMB.toFixed(5)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Margin:</span>
                                <span className="ml-2 font-semibold">{plan.marginPercent}%</span>
                              </div>
                            </div>
                          </div>
                          <div className="ml-4 flex gap-2">
                            <button
                              onClick={() => loadPlan(plan)}
                              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                            <button
                              onClick={() => deletePlan(plan.id, plan.name)}
                              disabled={loading}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete plan"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-gray-600 text-sm">
          <p>Zynalytics Wireless Rate Plan Calculator v2.0</p>
          <p className="mt-1">© 2025 Zardoz Group. All rights reserved</p>
        </div>
      </div>
    </div>
  );
}